import React, { useEffect, useRef, useState } from "react";
import { X, Upload, FileText, Shield, Lightbulb } from "lucide-react";
import {
  createCredential,
  updateCredential,
  uploadCredentialDocument,
  deleteCredentialDocument,
} from "../APIs/credentialApi";
import api from "../APIs/axios";

const CREDENTIAL_TYPES = [
  { value: "LICENSE", label: "License" },
  { value: "CERTIFICATE", label: "Certificate" },
  { value: "CERTIFICATION", label: "Certification" },
  { value: "INSURANCE", label: "Insurance" },
  { value: "DOCUMENT", label: "Document" },
  { value: "MALPRACTICE", label: "Malpractice" },
  { value: "DEA", label: "DEA" },
  { value: "NPI", label: "NPI" },
];

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

function formatDate(localDate) {
  if (!localDate) return "";
  if (typeof localDate === "string") return localDate.substring(0, 10);
  return localDate;
}

export default function CredentialModal({
  providerId,
  credential,
  onClose,
  onSaved,
}) {
  const isEdit = !!credential;
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    credentialType: credential?.credentialType ?? "",
    credentialName: credential?.credentialName ?? "",
    issuingOrganization: credential?.issuingOrganization ?? "",
    credentialNumber: credential?.credentialNumber ?? "",
    licenseState: credential?.licenseState ?? "",
    issueDate: formatDate(credential?.issueDate),
    expirationDate: formatDate(credential?.expirationDate),
    doesNotExpire: credential?.doesNotExpire ?? false,
    notes: credential?.notes ?? "",
  });

  const [existingDocs, setExistingDocs] = useState([]);
  const [deletedDocIds, setDeletedDocIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (isEdit && credential.credentialId) {
      api
        .get(`/api/v1/documents/credential/${credential.credentialId}`)
        .then((r) => setExistingDocs(r.data))
        .catch(() => {});
    }
  }, [isEdit, credential]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleFiles = (files) => {
    const valid = Array.from(files).filter((f) =>
      ["application/pdf", "image/jpeg", "image/png"].includes(f.type),
    );
    setNewFiles((prev) => [...prev, ...valid]);
  };

  const removeNewFile = (idx) =>
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));

  const markDeleteExisting = (docId) =>
    setDeletedDocIds((prev) => [...prev, docId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.credentialType || !form.credentialName || !form.issueDate) {
      setError("Credential Type, Name, and Issue Date are required.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        credentialType: form.credentialType,
        credentialName: form.credentialName,
        issuingOrganization: form.issuingOrganization || null,
        credentialNumber: form.credentialNumber || null,
        licenseState: form.licenseState || null,
        issueDate: form.issueDate,
        expirationDate: form.doesNotExpire ? null : form.expirationDate || null,
        doesNotExpire: form.doesNotExpire,
        notes: form.notes || null,
      };

      const saved = isEdit
        ? await updateCredential(credential.credentialId, providerId, payload)
        : await createCredential(providerId, payload);

      // Delete removed existing files
      await Promise.all(
        deletedDocIds.map((id) => deleteCredentialDocument(id, providerId)),
      );

      // Upload new files
      await Promise.all(
        newFiles.map((file) =>
          uploadCredentialDocument(
            saved.credentialId,
            providerId,
            file,
            saved.credentialType,
          ),
        ),
      );

      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Save failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? "Edit Credential" : "Add Credential"}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEdit
                ? "Update your credential information."
                : "Add a new professional credential to your profile."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 mt-0.5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex gap-0">
          {/* Left — form */}
          <div className="flex-1 px-6 py-5 space-y-6">
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </div>
            )}

            {/* Section 1 — Credential Information */}
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-3">
                1. Credential Information
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Credential Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.credentialType}
                    onChange={(e) => set("credentialType", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Select type</option>
                    {CREDENTIAL_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Credential Name / Title{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Medical License (State)"
                    value={form.credentialName}
                    onChange={(e) => set("credentialName", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Issuing Organization
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., California Medical Board"
                    value={form.issuingOrganization}
                    onChange={(e) => set("issuingOrganization", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Credential Number
                    </label>
                    <input
                      type="text"
                      placeholder="Enter credential number"
                      value={form.credentialNumber}
                      onChange={(e) => set("credentialNumber", e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      License State (if applicable)
                    </label>
                    <select
                      value={form.licenseState}
                      onChange={(e) => set("licenseState", e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Select state</option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 — Dates */}
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-3">
                2. Dates
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Issue Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.issueDate}
                    onChange={(e) => set("issueDate", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    value={form.expirationDate}
                    onChange={(e) => set("expirationDate", e.target.value)}
                    disabled={form.doesNotExpire}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.doesNotExpire}
                  onChange={(e) => set("doesNotExpire", e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-gray-600">Does not expire</span>
              </label>
              {form.doesNotExpire && (
                <p className="text-xs text-gray-400 mt-1 ml-6">
                  Check if this credential does not have an expiration date.
                </p>
              )}
            </div>

            {/* Section 3 — Document Upload */}
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-3">
                3. Document Upload
              </p>

              {/* Drag-and-drop zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  handleFiles(e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition ${
                  dragging
                    ? "border-teal-500 bg-teal-50"
                    : "border-gray-300 hover:border-teal-400"
                }`}
              >
                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  Drag and drop your file here, or{" "}
                  <span className="text-teal-600 font-medium">
                    click to browse
                  </span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PDF, JPG, PNG (Max 10MB)
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />

              {/* Existing docs (edit mode) */}
              {existingDocs
                .filter((d) => !deletedDocIds.includes(d.documentId))
                .map((doc) => (
                  <div
                    key={doc.documentId}
                    className="flex items-center gap-2 mt-2 bg-gray-50 border rounded-lg px-3 py-2"
                  >
                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700 flex-1 truncate">
                      {doc.fileName}
                    </span>
                    <button
                      type="button"
                      onClick={() => markDeleteExisting(doc.documentId)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

              {/* New files queued */}
              {newFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 mt-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2"
                >
                  <FileText className="w-4 h-4 text-teal-500 shrink-0" />
                  <span className="text-sm text-teal-700 flex-1 truncate">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeNewFile(idx)}
                    className="text-teal-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Section 4 — Additional Information */}
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-3">
                4. Additional Information (Optional)
              </p>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Notes
              </label>
              <textarea
                rows={3}
                placeholder="Add any additional notes about this credential"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
            </div>
          </div>

          {/* Right — info panel */}
          <div className="w-56 shrink-0 border-l bg-gray-50 px-4 py-6 space-y-5 rounded-tr-xl rounded-br-xl">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                <p className="text-sm font-semibold text-gray-800">
                  About Credentials
                </p>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Adding your credentials helps build trust with patients and
                ensures compliance with referral requirements.
              </p>
              <ul className="text-xs text-gray-500 space-y-1 list-none mt-2">
                {[
                  "Medical License",
                  "Board Certification",
                  "DEA Certificate",
                  "Insurance Documents",
                  "Other Professional Certificates",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-1">
                    <span className="text-teal-500 mt-0.5">•</span> {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-teal-600" />
                <p className="text-sm font-semibold text-gray-800">Tips</p>
              </div>
              <ul className="text-xs text-gray-500 space-y-2">
                {[
                  "Ensure your document is current and not expired.",
                  "Upload a clear, legible copy.",
                  "Credentials may be verified before being displayed on your profile.",
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-1.5">
                    <span className="text-teal-500 shrink-0 mt-0.5">✓</span>{" "}
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40"
          >
            {submitting
              ? "Saving..."
              : isEdit
                ? "Save Changes"
                : "Save Credential"}
          </button>
        </div>
      </div>
    </div>
  );
}
